import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  debounceTime,
} from "rxjs";
import { StreamState } from "./store/conference.store";
import { IPublishedStream } from "@auvious/rtc";

// import { StreamState } from "../models/stream.state";

export interface StreamTrack {
  id: string;
  streamId: string;
  streamState: StreamState;
  kind: "video" | "audio";
  source: "camera" | "screen";
  track: MediaStreamTrack;
  enabled: boolean;
}

export interface AssistantStore {
  cameraVideo?: StreamTrack;
  cameraAudio?: StreamTrack;
  screenVideo?: StreamTrack;
  screenAudio?: StreamTrack;
}

export interface ServerSyncState {
  tracksToAdd: StreamTrack[];
  tracksToRemove: StreamTrack[];
}

interface ITempStream extends IPublishedStream {
  response: {
    streamId: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class AssistantTrackManagerService {
  // Local stream state
  private localStreamState$ = new BehaviorSubject<AssistantStore>({});

  // Server stream state (what we believe is currently on the server)
  private serverStreamState$ = new BehaviorSubject<AssistantStore>({});

  // Track changes subject
  private trackChanges$ = new Subject<{
    action: "added" | "removed" | "enabled" | "disabled";
    track: StreamTrack;
  }>();

  // Sync state subject
  private syncState$ = new Subject<ServerSyncState>();

  // Debounce time for batching changes (in ms)
  private readonly SYNC_DEBOUNCE_TIME = 300;

  constructor() {
    // Set up automatic sync when local state changes
    this.setupAutoSync();
  }

  /**
   * Get current local stream state
   */
  getLocalState(): Observable<AssistantStore> {
    return this.localStreamState$.asObservable();
  }

  /**
   * Get current server stream state
   */
  getServerState(): Observable<AssistantStore> {
    return this.serverStreamState$.asObservable();
  }

  /**
   * Get track changes events
   */
  getTrackChanges(): Observable<{
    action: "added" | "removed" | "enabled" | "disabled";
    track: StreamTrack;
  }> {
    return this.trackChanges$.asObservable();
  }

  /**
   * Get sync state changes (what needs to be sent to server)
   */
  getSyncState(): Observable<ServerSyncState> {
    return this.syncState$.asObservable();
  }

  /**
   * Update local stream from MediaStream
   */
  updateLocalStream(
    stream: IPublishedStream,
    streamState: StreamState,
    source: "camera" | "screen"
  ): void {
    if (!stream) return;

    const currentState = { ...this.localStreamState$.value };

    // Process video tracks
    const videoTracks = stream.mediaStream.getVideoTracks();
    const videoTrackKey = source === "camera" ? "cameraVideo" : "screenVideo";

    if (videoTracks.length > 0) {
      const videoTrack = videoTracks[0];
      const streamTrack: StreamTrack = {
        id: `${source}-video-${videoTrack.id}`,
        streamId: this.getStreamId(stream),
        streamState: streamState,
        kind: "video",
        source,
        track: videoTrack,
        enabled: videoTrack.enabled,
      };

      // Check if this is a new track or state change
      const existingTrack = currentState[videoTrackKey];
      if (!existingTrack || existingTrack.track.id !== videoTrack.id) {
        if (existingTrack) {
          this.trackChanges$.next({ action: "removed", track: existingTrack });
        }
        currentState[videoTrackKey] = streamTrack;
        this.trackChanges$.next({ action: "added", track: streamTrack });
      } else if (existingTrack.enabled !== videoTrack.enabled) {
        existingTrack.enabled = videoTrack.enabled;
        this.trackChanges$.next({
          action: videoTrack.enabled ? "enabled" : "disabled",
          track: existingTrack,
        });
      }

      // Listen for track state changes
      // this.setupTrackListener(streamTrack);
    } else {
      // Remove video track if it exists
      if (currentState[videoTrackKey]) {
        this.trackChanges$.next({
          action: "removed",
          track: currentState[videoTrackKey]!,
        });
        delete currentState[videoTrackKey];
      }
    }

    // Process audio tracks
    const audioTracks = stream.mediaStream.getAudioTracks();
    const audioTrackKey = source === "camera" ? "cameraAudio" : "screenAudio";

    if (audioTracks.length > 0) {
      const audioTrack = audioTracks[0];
      const streamTrack: StreamTrack = {
        id: `${source}-audio-${audioTrack.id}`,
        streamId: this.getStreamId(stream),
        streamState: streamState,
        kind: "audio",
        source,
        track: audioTrack,
        enabled: audioTrack.enabled,
      };

      // Check if this is a new track or state change
      const existingTrack = currentState[audioTrackKey];
      if (!existingTrack || existingTrack.track.id !== audioTrack.id) {
        if (existingTrack) {
          this.trackChanges$.next({ action: "removed", track: existingTrack });
        }
        currentState[audioTrackKey] = streamTrack;
        this.trackChanges$.next({ action: "added", track: streamTrack });
      } else if (existingTrack.enabled !== audioTrack.enabled) {
        existingTrack.enabled = audioTrack.enabled;
        this.trackChanges$.next({
          action: audioTrack.enabled ? "enabled" : "disabled",
          track: existingTrack,
        });
      }

      // Listen for track state changes
      // this.setupTrackListener(streamTrack);
    } else {
      // Remove audio track if it exists
      if (currentState[audioTrackKey]) {
        this.trackChanges$.next({
          action: "removed",
          track: currentState[audioTrackKey]!,
        });
        delete currentState[audioTrackKey];
      }
    }

    this.localStreamState$.next(currentState);
  }

  /**
   * Remove stream tracks by source
   */
  removeStreamBySource(source: "camera" | "screen"): void {
    const currentState = { ...this.localStreamState$.value };

    const videoKey = source === "camera" ? "cameraVideo" : "screenVideo";
    const audioKey = source === "camera" ? "cameraAudio" : "screenAudio";

    if (currentState[videoKey]) {
      this.trackChanges$.next({
        action: "removed",
        track: currentState[videoKey]!,
      });
      delete currentState[videoKey];
    }

    if (currentState[audioKey]) {
      this.trackChanges$.next({
        action: "removed",
        track: currentState[audioKey]!,
      });
      delete currentState[audioKey];
    }

    this.localStreamState$.next(currentState);
  }

  /**
   * Toggle track enabled state
   */
  toggleTrack(
    source: "camera" | "screen",
    kind: "video" | "audio",
    enabled: boolean
  ): void {
    const currentState = { ...this.localStreamState$.value };
    const trackKey = `${source}${
      kind.charAt(0).toUpperCase() + kind.slice(1)
    }` as keyof AssistantStore;
    const track = currentState[trackKey];

    if (track && track.enabled !== enabled) {
      track.enabled = enabled;
      track.track.enabled = enabled;
      this.trackChanges$.next({
        action: enabled ? "enabled" : "disabled",
        track,
      });
      this.localStreamState$.next(currentState);
    }
  }

  /**
   * Get tracks that should be sent to server based on current requirements
   */
  getTracksForServer(requirements: {
    useCamera?: { video: boolean; audio: boolean };
    useScreen?: { video: boolean; audio: boolean };
  }): StreamTrack[] {
    const currentState = this.localStreamState$.value;
    const tracks: StreamTrack[] = [];

    // Add camera tracks if required
    if (requirements.useCamera) {
      if (requirements.useCamera.video && currentState.cameraVideo?.enabled) {
        tracks.push(currentState.cameraVideo);
      }
      if (requirements.useCamera.audio && currentState.cameraAudio?.enabled) {
        tracks.push(currentState.cameraAudio);
      }
    }

    // Add screen tracks if required
    if (requirements.useScreen) {
      if (requirements.useScreen.video && currentState.screenVideo?.enabled) {
        tracks.push(currentState.screenVideo);
      }
      if (requirements.useScreen.audio && currentState.screenAudio?.enabled) {
        tracks.push(currentState.screenAudio);
      }
    }

    return tracks;
  }

  /**
   * Sync local state with server
   */
  syncWithServer(requiredTracks: StreamTrack[]): void {
    const currentServerState = this.serverStreamState$.value;
    const serverTracks = this.getTracksFromState(currentServerState);

    // Calculate what needs to be added and removed
    const tracksToAdd = requiredTracks.filter((localTrack) => {
      const serverTrack = serverTracks.find((st) => st.id === localTrack.id);
      // Add if track doesn't exist on server OR if it exists but with different enabled state
      return !serverTrack || serverTrack.enabled !== localTrack.enabled;
    });

    const tracksToRemove = serverTracks.filter((serverTrack) => {
      const requiredTrack = requiredTracks.find(
        (rt) => rt.id === serverTrack.id
      );
      // Remove if track is not required OR if it exists but with different enabled state
      return (
        !requiredTrack ||
        (requiredTrack && requiredTrack.enabled !== serverTrack.enabled)
      );
    });

    // Emit sync state if there are changes
    if (tracksToAdd.length > 0 || tracksToRemove.length > 0) {
      this.syncState$.next({
        tracksToAdd,
        tracksToRemove,
      });
    }
  }

  /**
   * Update server state after successful sync
   */
  updateServerState(syncState: ServerSyncState): void {
    const serverState = this.serverStreamState$.value;

    syncState.tracksToRemove.forEach((track) => {
      const key = `${track.source}${
        track.kind.charAt(0).toUpperCase() + track.kind.slice(1)
      }` as keyof AssistantStore;
      delete serverState[key];
    });

    syncState.tracksToAdd.forEach((track) => {
      const key = `${track.source}${
        track.kind.charAt(0).toUpperCase() + track.kind.slice(1)
      }` as keyof AssistantStore;
      serverState[key] = { ...track }; // Create a copy to avoid reference issues
    });

    this.serverStreamState$.next(serverState);
  }

  /**
   * Get what the server state should be after applying required tracks
   */
  getExpectedServerState(requiredTracks: StreamTrack[]): AssistantStore {
    const expectedState: AssistantStore = {};

    requiredTracks.forEach((track) => {
      const key = `${track.source}${
        track.kind.charAt(0).toUpperCase() + track.kind.slice(1)
      }` as keyof AssistantStore;
      expectedState[key] = { ...track };
    });

    return expectedState;
  }

  /**
   * Clear all states
   */
  clear(): void {
    this.localStreamState$.next({});
    this.serverStreamState$.next({});
  }

  /*
  private setupTrackListener(streamTrack: StreamTrack): void {
    const track = streamTrack.track;

    // Listen for track ended event
    track.addEventListener("ended", () => {
      this.removeTrackById(streamTrack.id);
    });

    // Listen for mute/unmute events
    track.addEventListener("mute", () => {
      streamTrack.enabled = false;
      this.trackChanges$.next({ action: "disabled", track: streamTrack });
    });

    track.addEventListener("unmute", () => {
      streamTrack.enabled = true;
      this.trackChanges$.next({ action: "enabled", track: streamTrack });
    });
  }
  */

  /*
  private removeTrackById(trackId: string): void {
    const currentState = { ...this.localStreamState$.value };
    let removedTrack: StreamTrack | undefined;

    // Find and remove the track
    Object.keys(currentState).forEach((key) => {
      const track = currentState[key as keyof AssistantStore];
      if (track?.id === trackId) {
        removedTrack = track;
        delete currentState[key as keyof AssistantStore];
      }
    });

    if (removedTrack) {
      this.trackChanges$.next({ action: "removed", track: removedTrack });
      this.localStreamState$.next(currentState);
    }
  }
    */

  private getTracksFromState(state: AssistantStore): StreamTrack[] {
    return Object.values(state).filter(
      (track) => track !== undefined
    ) as StreamTrack[];
  }

  private getStreamId(stream: IPublishedStream) {
    return (stream as ITempStream).response.streamId;
  }

  private setupAutoSync(): void {
    // Auto-sync when local state changes (debounced)
    this.localStreamState$
      .pipe(
        debounceTime(this.SYNC_DEBOUNCE_TIME),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((localState) => {
        // This is where you'd implement your auto-sync logic
        // For now, we just emit the current local tracks
        // const localTracks = this.getTracksFromState(localState);
        // You can customize this logic based on your requirements
        // For example, you might want to sync all available tracks
        // or only tracks that meet certain criteria
      });
  }
}
